import moment from "moment";
import React, { createContext, useEffect, useMemo, useState } from "react";
import { BiMenu } from "react-icons/bi";
import { Container, Nav, Navbar } from "react-bootstrap";
import { BrowserRouter } from "react-router-dom";
import { ScaleLoader } from "react-spinners";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import "./App.scss";
import FormNavbarActions from "./components/Form/FormNavbarActions";
import { LS_REDIRECT_URL_KEY, LS_FORM_PERMISSIONS_KEY } from "./core/constants/common";
import UserService from "./core/services/UserService";
import Utils from "./core/utilities/Utils";
import SharedWith from "./pages/SharedWith/SharedWith";
import RoutesList from "./router";
import ThemesToggleBox from "./ThemesToggle";
export const LoaderContext = createContext();
export const FormContext = createContext();
export const GlbFormContext = createContext();
export const AssessmentShareTokenContext = createContext();
export const AssessmentFormShareDetailsContext = createContext();
export const FormSubmissionContext = createContext();
export const AppEventContext = createContext();
export const FormTitleContext = createContext();
export const SidebarContext = createContext();
export const CompanyContext = createContext();

const arr = [
  {
    id: 1,
    val: "Ananthu",
  },
  {
    id: 2,
    val: "Ananthu2",
  },
  {
    id: 3,
    val: "Ananthu3",
  },
];
function App() {
  const [userFullName, setUserFullName] = useState();
  const [redirectUrl, setRedirectUrl] = useState();
  const [loaderState, setLoaderState] = useState();
  const [showSharedWithModal, setShowSharedWithModal] = useState(false);
  let [color, setColor] = useState("#383cc1");
  const [formObj, setFormObj] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_FORM_PERMISSIONS_KEY);
      return stored ? JSON.parse(stored) : "";
    } catch (e) {
      return "";
    }
  });
  const [glbForm, setGlbForm] = useState("");
  const [shareToken, setShareToken] = useState("");
  const [shareDetails, setShareDetails] = useState({});
  const [formSubmissionData, setFormSubmissionData] = useState({});
  const [appEventData, setAppEventData] = useState({ name: '', data: null });
  const [sidebarDisplay, setSidebarDisplay] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth > 1024 : true)
  );
  const [companyId, setCompanyId] = useState(-1);

  useEffect(() => {
    const handleResize = () => {
      setSidebarDisplay(window.innerWidth > 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentURL = window.location.href;
  const isSubmission = currentURL.includes('submit') || currentURL.includes('review');
  const isOpenForm = currentURL.includes('/open-form/');
  const isApprovalForm = currentURL.includes('/review/');
  const isBuildMode = currentURL.includes('form_edit');
  const isPreview = currentURL.includes('form_preview');
  const useNewBuilder = process.env.REACT_APP_NEW_BUILDER !== 'false';
  const hideLegacyHeader = (isBuildMode || isPreview) && useNewBuilder;

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    // Remove maximized-chart-card class from any elements
    const elementsWithClass = document.querySelectorAll('.maximized-chart-card');
    elementsWithClass.forEach(element => {
      element.classList.remove('maximized-chart-card');
    });
  }, []);

  const getUser = () => {
    if (isOpenForm) return;

    UserService.getUserDetails()
      .then((response) => {
        setUserFullName(response.data.fullName);
        setRedirectUrl(response.data.redirectUrl);
        localStorage.setItem(LS_REDIRECT_URL_KEY, response.data.redirectUrl);
        setCompanyId(response.data.companyId ?? 0);        
      })
      .catch((error) => {
      });
  };
  const callbackSharedWithModal = () => {
    setShowSharedWithModal(false);
  };
  const handleSharedWith = () => {
    setShowSharedWithModal(true);
  };
  const handleExit = () => {
    const url = Utils.getFormattedFormBuilderExitURL();
    if (!url) return;

    window.location.href = url;
  };

  const getAssessmentPeriodLabel = (period, frequency) => {
    const endDate = moment(period, 'YYYY-MM-DD');

    const formatRange = (startDate, endDate) => {
      if (startDate.year() === endDate.year()) {
        return `${startDate.format('MMMM YYYY')} to ${endDate.format('MMMM YYYY')}`;
      }
      return `${startDate.format('MMMM YYYY')} to ${endDate.format('MMMM YYYY')}`;
    };

    if (frequency === 'QUARTERLY') {
      const startDate = endDate.clone().subtract(2, 'months');
      return formatRange(startDate, endDate);
    }

    if (frequency === 'HALF_YEARLY') {
      const startDate = endDate.clone().subtract(5, 'months');
      return formatRange(startDate, endDate);
    }

    if (frequency === 'YEARLY') {
      const startDate = endDate.clone().subtract(11, 'months');
      return formatRange(startDate, endDate);
    }

    return endDate.format('MMMM YYYY');
  };

  const getFormTitle = () => {
    if (isSubmission) {
      return glbForm?.assessment
        ? glbForm?.formType === 'SURVEY'
          ? `${glbForm?.company?.name ?? ''} Materiality survey for ${moment(glbForm?.assessment?.period, 'YYYY-MM-DD').format("YYYY")}`
          : `${glbForm?.company?.name ?? ''} ESG assessment for the period ${getAssessmentPeriodLabel(glbForm?.assessment?.period, glbForm?.assessment?.frequency)}`
        : 'ESG Assessment';
    } else {
      return 'Forms';
    }
  };

  const formContextValue = useMemo(() => ({ formObj, setFormObj }), [formObj]);
  const companyContextValue = useMemo(
    () => ({ companyId, setCompanyId, userFullName }),
    [companyId, userFullName],
  );

  return (
    <BrowserRouter>
      <FormContext.Provider value={formContextValue}>
        <GlbFormContext.Provider
          value={{
            glbForm,
            setGlbForm,
          }}
        >
          <CompanyContext.Provider value={companyContextValue}>
            <FormSubmissionContext.Provider
              value={{
                formSubmissionData,
                setFormSubmissionData,
              }}>
              <AppEventContext.Provider
                value={{
                  appEventData,
                  setAppEventData,
                }}>
                <AssessmentShareTokenContext.Provider
                  value={{
                    shareToken,
                    setShareToken,
                  }}
                >
                  <AssessmentFormShareDetailsContext.Provider
                    value={{
                      shareDetails,
                      setShareDetails
                    }}>
                    <SidebarContext.Provider
                      value={{
                        sidebarDisplay,
                        setSidebarDisplay,
                      }}>
                      <FormTitleContext.Provider
                        value={{
                          formTitle:
                            getFormTitle()
                        }}>
                      <div className="App">
                        <div className="wrapper">
                          <LoaderContext.Provider
                            value={{
                              loaderState,
                              setLoaderState,
                            }}
                          >
                            {!hideLegacyHeader && (
                            <div className="header header-modern">

                              <Navbar className="header__navbar">
                                <Container fluid className="header__container">
                                  <Navbar.Brand className="header__brand">
                                    <button
                                      type="button"
                                      className="Header_menu_icon"
                                      aria-label={sidebarDisplay ? 'Close menu' : 'Open menu'}
                                      aria-expanded={sidebarDisplay}
                                      aria-controls="form-sidebar"
                                      onClick={() => setSidebarDisplay(prev => !prev)}
                                    >
                                      <BiMenu />
                                    </button>
                                    {
                                      glbForm?.company &&
                                      <span className="header__logo">
                                        <img src={UserService.getCompanyLogo(glbForm?.company?.id)}
                                          alt={glbForm?.company?.name}
                                        />
                                      </span>
                                    }
                                  </Navbar.Brand>
                                  <div className="header__actions">
                                    <Nav className="header__nav">
                                      {userFullName && (
                                        <div className="header__user">
                                          <span className="header__user-avatar">
                                            {userFullName.charAt(0).toUpperCase()}
                                          </span>
                                          <span className="header__user-name">{userFullName}</span>
                                        </div>
                                      )}
                                      {redirectUrl && (
                                        <button
                                          type="button"
                                          className="header__back-btn"
                                          onClick={handleExit}
                                        >
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 12H5M12 19l-7-7 7-7" />
                                          </svg>
                                          Back
                                        </button>
                                      )}
                                      <FormNavbarActions />
                                    </Nav>
                                    <div className="header__theme">
                                      <ThemesToggleBox />
                                    </div>
                                  </div>
                                </Container>
                              </Navbar>
                              {showSharedWithModal && (
                                <SharedWith
                                  modalShow={showSharedWithModal}
                                  callbackModal={callbackSharedWithModal}
                                />
                              )}
                            </div>
                            )}
                            <RoutesList />
                            <ToastContainer autoClose={700} />
                            {loaderState?.display && (
                              <div className="loader-custom" role="status" aria-live="polite" aria-busy="true">
                                <ScaleLoader color={color} loading={true} size={25} />
                                {
                                  loaderState?.type === 'TEXTUAL' &&
                                  <div className="d-block" >
                                    <h5>Please wait while we load your data.</h5>
                                  </div>
                                }
                              </div>
                            )}
                          </LoaderContext.Provider>
                        </div>
                      </div>
                      </FormTitleContext.Provider>
                    </SidebarContext.Provider>
                  </AssessmentFormShareDetailsContext.Provider>
                </AssessmentShareTokenContext.Provider>
              </AppEventContext.Provider>
            </FormSubmissionContext.Provider>
          </CompanyContext.Provider>
        </GlbFormContext.Provider>
      </FormContext.Provider>
    </BrowserRouter>
  );
}

export default App;
